import axios from 'axios';
import { AccountInfoResponse, RecommendationResponse } from 'types/billing';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  CancelSubscriptionResponse,
  CreateBillingPortalSessionResponse,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  GetBillingInfoResponse,
  GetBillingQuotaBalanceResponse,
  GetBillingAnalyticsParams,
  GetBillingAnalyticsResponse,
  GetBillingReviewsParams,
  GetBillingReviewsResponse,
  GetCheckoutPackagesResponse,
  MigrateConversionRequest,
  MigrateConversionResponse,
  OverageInfo,
  UpdateBillingInfoRequest,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosBillingInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosBillingInstance);

export class BillingClient extends BaseHttpClient {
  constructor() {
    super(axiosBillingInstance, {
      defaultExtractNestedData: false,
    });
  }

  static override instance = new BillingClient();

  /*****************
   *      GET
   *****************/
  static async getBillingQuotaBalance() {
    return await BillingClient.get<GetBillingQuotaBalanceResponse>(
      '/quota/balance',
    );
  }

  static async getAccountInfo() {
    return await BillingClient.get<AccountInfoResponse>('/account-info');
  }

  static async getRecommendation() {
    return await BillingClient.get<RecommendationResponse>('/recommendation');
  }

  static async getBillingInfo() {
    return await BillingClient.get<GetBillingInfoResponse>('/billing-info');
  }

  // Self-serve plan ladder for the conversion-popup carousel (PLT-4162),
  // sourced live from Stripe. Global + static, so callers cache it hard.
  static async getCheckoutPackages() {
    return await BillingClient.get<GetCheckoutPackagesResponse>(
      '/checkout/packages',
    );
  }

  static async getBillingAnalytics(params: GetBillingAnalyticsParams) {
    return await BillingClient.get<GetBillingAnalyticsResponse>(
      '/metering/analytics',
      { params },
    );
  }

  static async getBillingReviews(params: GetBillingReviewsParams) {
    return await BillingClient.get<GetBillingReviewsResponse>(
      '/metering/reviews',
      { params },
    );
  }

  /*****************
   *      POST
   *****************/
  // Self-serve conversion checkout (PLT-3715). Admin-only + self-serve + FF
  // gated on the BE; returns the Stripe-hosted Checkout URL the popup redirects
  // to. Distinct from the legacy PlatformClient /teams/create-checkout-session.
  static async createCheckoutSession(payload: CreateCheckoutSessionRequest) {
    return await BillingClient.post<CreateCheckoutSessionResponse>(
      '/checkout/session',
      payload,
    );
  }

  // Paid-cohort self-serve migration (the "Move"): swaps the legacy sub for a
  // usage-based self-serve sub on the card on file, server-side (no checkout).
  // Takes planSku (credits); the BE resolves it to a price id.
  static async migrateConversion(payload: MigrateConversionRequest) {
    return await BillingClient.post<MigrateConversionResponse>(
      '/conversions/move',
      payload,
    );
  }

  // Reverse a Move parked PAYMENT_ACTION_REQUIRED (off-session charge needs 3DS),
  // on the user's confirmation (no body): cancel the legacy sub, refund the unused
  // time, stamp the account self-serve, and return REVERSED so the FE can hand off
  // to a standard self-serve checkout.
  static async reverseMove() {
    return await BillingClient.post<MigrateConversionResponse>(
      '/conversions/move/reverse',
      {},
    );
  }

  static async createBillingPortalSession() {
    return await BillingClient.post<CreateBillingPortalSessionResponse>(
      '/billing-portal',
      {},
    );
  }

  static async cancelSubscription() {
    return await BillingClient.post<CancelSubscriptionResponse>(
      '/subscription/cancel',
      {},
    );
  }

  static async reactivateSubscription() {
    return await BillingClient.post<void>('/subscription/reactivate', {});
  }

  // Self-serve downgrade (PLT-4468): schedule a move to a smaller credit pack at
  // period end via a Stripe Subscription Schedule. The user keeps their current
  // pool until renewal. targetProductId is the destination pack.
  static async changePlan(targetProductId: string) {
    return await BillingClient.post<void>('/subscription/change-plan', {
      targetProductId,
    });
  }

  // Cancel a pending scheduled plan change, restoring the active subscription.
  static async cancelScheduledChange() {
    return await BillingClient.post<void>(
      '/subscription/change-plan/cancel',
      {},
    );
  }

  /*****************
   *      PATCH
   *****************/
  // Mark trial_banner_shown=true on the account's active conversion record so the
  // migration welcome popup never reappears. Idempotent + fail-soft on the BE.
  static async markConversionBannerShown() {
    return await BillingClient.patch<void>(
      '/account-info/trial-banner-shown',
      {},
    );
  }
  // Admin-only update of the account's user overage charge limit, in whole USD.
  // The body is snake_case (extra=forbid on the BE); the response echoes the
  // resolved cap (USD ceiling + credit equivalent) in camelCase.
  static async updateBillingInfo(overageChargeLimit: number) {
    const payload: UpdateBillingInfoRequest = {
      overage_charge_limit: overageChargeLimit,
    };

    return await BillingClient.patch<OverageInfo>('/billing-info', payload);
  }
}
